import * as React from 'react';
import { APIProvider, APIProviderContext } from '@vis.gl/react-google-maps';
import { MAP_LIBRARIES } from 'maps/mapsConfig';
import { useAppSettings } from 'selectors/settingsSelector';
import MapsLoadStateBridge from 'maps/MapsLoadStateBridge';

interface Props {
  /** Fired once the Maps JS API has finished loading. */
  onLoad?(): void;
}

/**
 * Single `<APIProvider>` wrapper used by every maps-backed route/form in the
 * consumer app on the flag-ON path. Centralizing the `apiKey` + `libraries` here
 * guarantees every call site loads the API with an identical param set (so
 * vis.gl loads the Maps JS script once process-wide and reuses it across
 * mounts), silencing the cross-route "already loaded with different parameters"
 * warning. It also mounts {@link MapsLoadStateBridge} so the existing Redux
 * load-state + `GoogleMapsContext` keep working for downstream consumers.
 */
const MapsApiProvider: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  onLoad,
  children,
}) => {
  const settings = useAppSettings();
  // A parent `MapsApiProvider` may have already mounted an `<APIProvider>`
  // (e.g. `CulinaryTravelLogSection` wraps `<ProfileMap>` in `<LoadGoogleMaps>`,
  // and `ProfileMapNew` also self-wraps a `MapsApiProvider`). vis.gl supports a
  // single provider process-wide; nesting a second one makes the inner provider
  // race its async API-load against unmount ("Can't perform a React state update
  // on an unmounted component"). When already inside a provider, reuse it.
  const alreadyInsideApiProvider = React.useContext(APIProviderContext) !== null;

  if (alreadyInsideApiProvider) {
    return <>{children}</>;
  }

  return (
    <APIProvider apiKey={settings.GOOGLE_MAPS_KEY} libraries={MAP_LIBRARIES} onLoad={onLoad}>
      <MapsLoadStateBridge />
      {children}
    </APIProvider>
  );
};

export default MapsApiProvider;
